import { useState, useCallback, useEffect } from 'react';
import { Loader2, RefreshCw, ClipboardList, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';
import { InvoiceDirectionReconciliationStrip } from './InvoiceDirectionReconciliationStrip';
import { taxonomyLabel } from '../../lib/invoiceTaxonomy';

function fmtInt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

function BreakdownRow({ label, counts, labelFor }) {
  const entries = Object.entries(counts || {});
  if (entries.length === 0) return null;
  return (
    <div className="space-y-1">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="flex flex-wrap gap-1">
        {entries.map(([key, count]) => (
          <Badge key={key} variant="outline" className="text-[10px]">
            {labelFor ? labelFor(key) : key}: {count}
          </Badge>
        ))}
      </div>
    </div>
  );
}

// Mirror of ClientInvoiceCensusPanel for the payable/inbound (Supplier)
// side. Written alongside the invoice-direction canonicalization fixing
// Xero/Pennylane Supplier invoices that were invisible on both Invoices
// page tabs -- this panel's total is directly comparable to what the
// Supplier Invoices tab itself shows.
export function SupplierInvoiceCensusPanel() {
  const [census, setCensus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const runCensus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await diagnosticsAPI.supplierInvoiceCensus();
      setCensus(data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { runCensus(); }, [runCensus]);

  const zeroFlags = census?.zero_count_expected_sources || [];

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <ClipboardList className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Supplier Invoice Census</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Read-only breakdown of the Supplier Invoices population (source, review status,
          taxonomy, matching status). Query mirrors the Supplier Invoices page's own
          direction filter exactly, so this total is directly comparable to what that
          page shows. Diagnostic only -- no Apply, this never writes.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2">
          <Button data-testid="supplier-invoice-census-refresh" onClick={runCensus} disabled={loading} size="sm" variant="outline">
            {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
            Refresh
          </Button>
          {census?.generated_at && (
            <span className="text-xs text-muted-foreground">
              as of {new Date(census.generated_at).toLocaleString('en-GB')}
            </span>
          )}
        </div>

        {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}

        {census && (
          <div className="text-xs space-y-3" data-testid="supplier-invoice-census-summary">
            <div>
              Total Supplier Invoices: <strong>{fmtInt(census.total)}</strong>
            </div>

            {zeroFlags.length > 0 && (
              <div
                className="flex items-start gap-1 p-2 rounded border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/30 text-amber-800 dark:text-amber-300"
                data-testid="supplier-invoice-census-zero-flag"
              >
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>
                  Expected source(s) with zero Supplier Invoices: <strong>{zeroFlags.join(', ')}</strong>.
                  Worth checking whether the sync for this source is actually running.
                </span>
              </div>
            )}

            <BreakdownRow label="By source" counts={census.by_source} />
            <BreakdownRow label="By taxonomy" counts={census.by_taxonomy} labelFor={taxonomyLabel} />
            <BreakdownRow label="By review status" counts={census.by_review_status} />
            <BreakdownRow label="By matching status" counts={census.by_matching_status} />

            {census.unassigned_entity_count > 0 && (
              <div>
                Unresolved entity: <strong>{fmtInt(census.unassigned_entity_count)}</strong>
              </div>
            )}

            <InvoiceDirectionReconciliationStrip reconciliation={census.direction_reconciliation} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
